<template>
  <section class="rounded-lg border border-slate-200 bg-white">
    <div class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
      <div>
        <h4 class="text-sm font-semibold text-slate-900">{{ title }}</h4>
        <p class="mt-0.5 text-xs text-slate-500">选择本地文件，上传成功后自动绑定到当前业务。</p>
      </div>
      <button
        v-if="canManage"
        type="button"
        class="btn-secondary inline-flex items-center gap-1.5"
        :disabled="!bizId"
        @click="toggleUploader"
      >
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
        </svg>
        上传附件
      </button>
    </div>

    <div v-if="showBinder && canManage" class="border-b border-slate-200 bg-slate-50 p-4">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div class="form-label">附件文件</div>
          <p class="mt-0.5 text-xs text-slate-500">
            文件会先上传到素材库，再绑定到当前{{ bizType === 'CONTRACT' ? '合同' : '申请' }}。
            <span v-if="bizType === 'CONTRACT'">合同附件仅支持 PDF、Word 文档或图片。</span>
          </p>
        </div>
        <button type="button" class="btn-secondary" @click="triggerFileInput">选择文件</button>
      </div>
      <input ref="fileInput" type="file" multiple class="hidden" :accept="fileAccept" @change="handleFileSelect">

      <div v-if="pendingAttachmentFiles.length" class="mt-3 space-y-3">
        <label class="block">
          <span class="form-label">存放素材文件夹 <span class="text-red-500">*</span></span>
          <select v-model.number="assetFolderId" class="form-select-native w-full" :disabled="folderLoading">
            <option :value="null">{{ folderLoading ? '正在加载文件夹...' : '请选择文件夹' }}</option>
            <option v-for="folder in assetFolderOptions" :key="folder.id" :value="folder.id">{{ folder.path }}</option>
          </select>
        </label>
        <div class="space-y-2">
          <div
            v-for="(file, index) in pendingAttachmentFiles"
            :key="`${file.name}-${file.size}-${index}`"
            class="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2"
          >
            <div class="min-w-0">
              <div class="truncate text-sm font-medium text-slate-900">{{ file.name }}</div>
              <div class="text-xs text-slate-500">{{ formatFileSize(file.size) }}</div>
            </div>
            <button type="button" class="text-sm text-red-600 hover:text-red-700" @click="removeAttachmentFile(index)">移除</button>
          </div>
        </div>
      </div>

      <div v-else class="mt-3 rounded-md border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-500">
        暂未选择文件
      </div>

      <div class="mt-3 flex justify-end gap-2">
        <button type="button" class="btn-secondary" :disabled="submitting" @click="cancelUploader">取消</button>
        <button type="button" class="btn-primary" :disabled="submitting" @click="handleBind">
          {{ submitting ? '上传中...' : '上传并绑定' }}
        </button>
      </div>
    </div>

    <div class="p-4">
      <div v-if="loading" class="py-8 text-center text-sm text-slate-500">加载附件中...</div>
      <div v-else-if="attachments.length === 0" class="py-8 text-center text-sm text-slate-500">暂无附件</div>
      <div v-else class="space-y-2">
        <div v-for="item in attachments" :key="item.id" class="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
          <div class="min-w-0">
            <div class="truncate text-sm font-medium text-slate-900">assetId: {{ item.assetId }}</div>
            <div class="mt-0.5 text-xs text-slate-500">
              {{ item.status || 'ACTIVE' }} · {{ item.createTime || '--' }}
            </div>
          </div>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="table-action-button is-primary"
              title="预览"
              @click="previewWealthAttachment(item)"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
              </svg>
            </button>
            <button
              v-if="canManage && item.status !== 'VOIDED'"
              type="button"
              class="table-action-button is-danger"
              title="作废"
              @click="handleVoid(item)"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <div v-if="showPreviewModal" class="fixed inset-0 z-[90] bg-black/70">
    <div class="absolute left-4 right-4 top-4 z-10 flex items-center justify-between gap-3 text-white">
      <div class="min-w-0">
        <div class="truncate text-sm font-medium">{{ previewState.name || '附件预览' }}</div>
        <div v-if="previewState.message" class="mt-0.5 text-xs text-white/70">{{ previewState.message }}</div>
      </div>
      <div class="flex items-center gap-2">
        <button
          v-if="previewState.sourceUrl"
          type="button"
          class="rounded-md bg-white/10 px-3 py-1.5 text-xs hover:bg-white/20"
          @click="openPreviewSource"
        >
          下载查看
        </button>
        <button
          type="button"
          class="rounded-full bg-white/10 p-2 hover:bg-white/20"
          title="关闭预览"
          @click="closePreviewModal"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>
    <div class="flex h-full w-full items-center justify-center p-6 pt-16">
      <div v-if="previewState.loading" class="flex flex-col items-center gap-3 text-white/90">
        <div class="h-10 w-10 animate-spin rounded-full border-2 border-white/40 border-t-white"></div>
        <span class="text-sm">正在加载预览...</span>
      </div>
      <div v-else-if="previewState.url" class="flex h-full w-full items-center justify-center">
        <img
          v-if="previewState.type.startsWith('image/')"
          :src="previewState.url"
          :alt="previewState.name"
          class="max-h-[85vh] max-w-[90vw] object-contain drop-shadow-lg"
        >
        <iframe
          v-else
          :src="previewState.url"
          class="h-[85vh] w-[90vw] rounded-lg bg-white"
        ></iframe>
      </div>
      <div v-else class="rounded-lg bg-white/10 px-6 py-5 text-center text-sm text-white/80">
        {{ previewState.message || '暂无可预览内容' }}
      </div>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios'
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { cancelUploadSession, confirmUploadSession, createUploadSession, getFolderTree, previewAsset } from '@/api/assets'
import {
  bindWealthContractAttachments,
  bindWealthOperationApplyAttachments,
  getWealthAttachments,
  voidWealthContractAttachment,
  voidWealthOperationApplyAttachment
} from '@/api/wealth'
import { useUserStore } from '@/stores/user'
import { WEB_CLIENT_TYPE, getWebDeviceId } from '@/utils/auth-session'

const props = defineProps({
  title: {
    type: String,
    default: '附件'
  },
  bizType: {
    type: String,
    required: true
  },
  bizId: {
    type: Number,
    default: null
  },
  canManage: {
    type: Boolean,
    default: false
  }
})

const CONTRACT_ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,image/*'
const CONTRACT_ATTACHMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']

const attachments = ref([])
const loading = ref(false)
const submitting = ref(false)
const showBinder = ref(false)
const fileInput = ref(null)
const pendingAttachmentFiles = ref([])
const assetFolderId = ref(null)
const assetFolderOptions = ref([])
const folderLoading = ref(false)
const currentUploadSessionId = ref(null)
const fileAccept = computed(() => props.bizType === 'CONTRACT' ? CONTRACT_ATTACHMENT_ACCEPT : undefined)
const showPreviewModal = ref(false)
const previewState = reactive({
  loading: false,
  url: '',
  objectUrl: false,
  sourceUrl: '',
  type: '',
  name: '',
  message: ''
})
const PREVIEWABLE_ATTACHMENT_EXTENSIONS = new Set(['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff'])

async function loadAttachments() {
  if (!props.bizType || !props.bizId) {
    attachments.value = []
    return
  }
  loading.value = true
  try {
    const result = await getWealthAttachments({
      bizType: props.bizType,
      bizId: props.bizId,
      status: ''
    })
    attachments.value = Array.isArray(result) ? result : []
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

function toggleUploader() {
  showBinder.value = !showBinder.value
  if (showBinder.value && pendingAttachmentFiles.value.length) {
    ensureAssetFoldersLoaded()
  }
}

function cancelUploader() {
  showBinder.value = false
  pendingAttachmentFiles.value = []
  if (fileInput.value) fileInput.value.value = ''
}

function triggerFileInput() {
  fileInput.value?.click()
}

function handleFileSelect(event) {
  const files = Array.from(event.target.files || [])
  if (files.length) {
    const acceptedFiles = files.filter(isAllowedAttachmentFile)
    if (acceptedFiles.length) {
      pendingAttachmentFiles.value = [...pendingAttachmentFiles.value, ...acceptedFiles]
      ensureAssetFoldersLoaded()
    }
  }
  event.target.value = ''
}

function isAllowedAttachmentFile(file) {
  if (props.bizType !== 'CONTRACT') return true
  return isAllowedContractAttachmentFile(file)
}

function isAllowedContractAttachmentFile(file) {
  const extension = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : ''
  const allowed = file.type?.startsWith('image/') || CONTRACT_ATTACHMENT_EXTENSIONS.includes(extension)
  if (!allowed) {
    ElMessage.warning('合同附件仅支持 PDF、Word 文档或图片')
  }
  return allowed
}

function removeAttachmentFile(index) {
  pendingAttachmentFiles.value.splice(index, 1)
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

function getAssetType(file) {
  if (file.type?.startsWith('image/')) return 'IMAGE'
  if (file.type?.startsWith('video/')) return 'VIDEO'
  return 'FILE'
}

function assetNameOf(file) {
  const dotIndex = file.name.lastIndexOf('.')
  return dotIndex > 0 ? file.name.slice(0, dotIndex) : file.name
}

async function ensureAssetFoldersLoaded() {
  if (assetFolderOptions.value.length || folderLoading.value) return
  folderLoading.value = true
  try {
    const tree = await getFolderTree()
    assetFolderOptions.value = flattenManageableFolders(tree || [])
    if (!assetFolderId.value && assetFolderOptions.value.length) {
      assetFolderId.value = assetFolderOptions.value[0].id
    }
  } catch {
    // request interceptor handles visible error
  } finally {
    folderLoading.value = false
  }
}

function flattenManageableFolders(nodes, parentPath = '') {
  return nodes.flatMap(node => {
    const path = parentPath ? `${parentPath} / ${node.name}` : node.name
    const current = node.permissionLevel === 'MANAGE' && !node.pathOnly ? [{ id: node.id, path }] : []
    return [...current, ...flattenManageableFolders(node.children || [], path)]
  })
}

async function handleBind() {
  if (!pendingAttachmentFiles.value.length) {
    ElMessage.warning('请选择附件文件')
    return
  }
  if (!assetFolderId.value) {
    ElMessage.warning('请选择附件存放文件夹')
    return
  }
  submitting.value = true
  try {
    const assetIds = await uploadPendingAttachmentFiles()
    const payload = { bizType: props.bizType, bizId: props.bizId, assetIds }
    if (props.bizType === 'CONTRACT') {
      await bindWealthContractAttachments(props.bizId, payload)
    } else {
      await bindWealthOperationApplyAttachments(props.bizId, payload)
    }
    ElMessage.success('附件已上传并绑定')
    pendingAttachmentFiles.value = []
    showBinder.value = false
    if (fileInput.value) fileInput.value.value = ''
    await loadAttachments()
  } catch (error) {
    console.error(error)
  } finally {
    submitting.value = false
  }
}

async function uploadPendingAttachmentFiles() {
  const assetIds = []
  for (const file of pendingAttachmentFiles.value) {
    assetIds.push(await uploadOneAttachmentFile(file))
  }
  return assetIds
}

async function uploadOneAttachmentFile(file) {
  if (!isAllowedAttachmentFile(file)) {
    throw new Error('unsupported attachment file type')
  }
  try {
    const credential = await createUploadSession({
      folderId: assetFolderId.value,
      assetType: getAssetType(file),
      originalName: file.name,
      mimeType: file.type || 'application/octet-stream',
      fileSize: file.size
    })
    currentUploadSessionId.value = credential.sessionId
    await axios.put(credential.uploadUrl, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        ...(credential.headers || {})
      }
    })
    const asset = await confirmUploadSession(credential.sessionId, {
      uploadToken: credential.uploadToken,
      name: assetNameOf(file),
      description: props.bizType === 'CONTRACT' ? '财富合同附件' : '财富申请附件',
      tagIds: []
    })
    currentUploadSessionId.value = null
    return asset.id
  } catch (error) {
    await cancelPendingUploadSession()
    throw error
  }
}

async function previewWealthAttachment(item) {
  if (!item?.assetId) {
    ElMessage.warning('附件缺少 assetId，无法预览')
    return
  }
  resetPreviewState()
  previewState.loading = true
  previewState.name = attachmentDisplayName(item)
  showPreviewModal.value = true
  try {
    const preview = await previewAsset(item.assetId)
    if (typeof preview === 'string') {
      openPreviewBlob(new Blob([preview], { type: 'text/plain;charset=UTF-8' }), 'text/plain;charset=UTF-8', {
        fileName: previewState.name
      })
      return
    }

    previewState.name = attachmentDisplayName(item, preview)
    const previewUrl = resolveAssetFileUrl(preview.previewUrl)
    previewState.sourceUrl = previewUrl
    const extension = attachmentFileExtension(item, preview)
    const declaredType = preview.contentType || inferContentType(extension)

    if (!previewUrl) {
      previewState.message = '文件不可访问'
      return
    }
    if (!isPreviewableAttachment(declaredType, extension)) {
      previewState.type = declaredType || 'application/octet-stream'
      previewState.message = '该文件类型暂不支持在线预览，请下载后查看'
      return
    }
    if (isExternalUrl(previewUrl)) {
      openPreviewUrl(previewUrl, declaredType, preview)
      return
    }

    try {
      const response = await fetchAssetPreviewBlob(previewUrl)
      const contentType = response?.headers?.['content-type'] || response?.data?.type || declaredType
      openPreviewBlob(response.data, contentType, preview)
    } catch (error) {
      if (isExternalUrl(previewUrl)) {
        openPreviewUrl(previewUrl, declaredType, preview)
        return
      }
      throw error
    }
  } catch (error) {
    const status = error?.response?.status
    if (status === 415) {
      previewState.message = '该文件类型暂不支持在线预览，请下载后查看'
      ElMessage.warning(previewState.message)
    } else if (status === 403 || status === 404) {
      previewState.message = '文件不可访问'
      ElMessage.error(previewState.message)
    } else {
      previewState.message = '预览失败，请稍后重试'
      ElMessage.error(previewState.message)
    }
  } finally {
    previewState.loading = false
  }
}

function resolveAssetFileUrl(url) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  const baseUrl = process.env.VUE_APP_API_BASE_URL || ''
  if (!baseUrl) {
    return url.startsWith('/') ? url : `/${url}`
  }
  return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`
}

function isExternalUrl(url) {
  if (!url) return false
  try {
    const parsed = new URL(url, window.location.href)
    return parsed.origin !== window.location.origin
  } catch {
    return false
  }
}

function getAuthHeaders() {
  const userStore = useUserStore()
  const headers = {}
  if (userStore.accessToken) {
    headers.Authorization = `${userStore.tokenType} ${userStore.accessToken}`
  }
  headers['X-Device-Id'] = getWebDeviceId()
  headers['X-Client-Type'] = WEB_CLIENT_TYPE
  return headers
}

function shouldAttachAuthHeaders(url) {
  if (!isExternalUrl(url)) return true
  const baseUrl = process.env.VUE_APP_API_BASE_URL || ''
  return Boolean(baseUrl && url.startsWith(baseUrl))
}

function fetchAssetPreviewBlob(url) {
  if (shouldAttachAuthHeaders(url)) {
    return axios.get(url, {
      responseType: 'blob',
      headers: getAuthHeaders()
    })
  }
  return axios.get(url, {
    responseType: 'blob'
  })
}

function attachmentDisplayName(item, preview = {}) {
  return preview.fileName || item?.fileName || item?.originalName || item?.name || `asset-${item?.assetId || 'preview'}`
}

function attachmentFileExtension(item, preview = {}) {
  const name = attachmentDisplayName(item, preview)
  const match = name.match(/\.([0-9a-z]+)$/i)
  return match ? match[1].toLowerCase() : ''
}

function inferContentType(extension) {
  if (!extension) return ''
  if (extension === 'pdf') return 'application/pdf'
  if (['jpg', 'jpeg'].includes(extension)) return 'image/jpeg'
  if (['png', 'gif', 'webp', 'bmp', 'tiff'].includes(extension)) return `image/${extension}`
  return ''
}

function isPreviewableAttachment(contentType, extension) {
  const normalizedType = String(contentType || '').toLowerCase()
  return normalizedType.startsWith('image/') ||
    normalizedType.includes('pdf') ||
    PREVIEWABLE_ATTACHMENT_EXTENSIONS.has(extension)
}

function openPreviewBlob(blob, contentType, preview = {}) {
  clearPreviewObjectUrl()
  previewState.url = URL.createObjectURL(blob)
  previewState.objectUrl = true
  previewState.type = contentType || preview.contentType || ''
  previewState.name = preview.fileName || previewState.name
  previewState.message = ''
}

function openPreviewUrl(url, contentType, preview = {}) {
  clearPreviewObjectUrl()
  previewState.url = url
  previewState.objectUrl = false
  previewState.type = contentType || preview.contentType || ''
  previewState.name = preview.fileName || previewState.name
  previewState.message = ''
}

function clearPreviewObjectUrl() {
  if (previewState.objectUrl && previewState.url) {
    URL.revokeObjectURL(previewState.url)
  }
  previewState.url = ''
  previewState.objectUrl = false
}

function resetPreviewState() {
  clearPreviewObjectUrl()
  previewState.loading = false
  previewState.sourceUrl = ''
  previewState.type = ''
  previewState.name = ''
  previewState.message = ''
}

function closePreviewModal() {
  showPreviewModal.value = false
  resetPreviewState()
}

function openPreviewSource() {
  if (!previewState.sourceUrl) return
  window.open(previewState.sourceUrl, '_blank', 'noopener')
}

async function cancelPendingUploadSession() {
  if (!currentUploadSessionId.value) return
  const sessionId = currentUploadSessionId.value
  currentUploadSessionId.value = null
  try {
    await cancelUploadSession(sessionId)
  } catch {
    // upload session cancel failure should not hide the original upload error
  }
}

async function handleVoid(item) {
  try {
    await ElMessageBox.confirm('确认作废该附件关系吗？', '作废附件', {
      confirmButtonText: '确认作废',
      cancelButtonText: '取消',
      type: 'warning'
    })
    const payload = { reason: '页面作废' }
    if (props.bizType === 'CONTRACT') {
      await voidWealthContractAttachment(props.bizId, item.id, payload)
    } else {
      await voidWealthOperationApplyAttachment(props.bizId, item.id, payload)
    }
    ElMessage.success('附件已作废')
    await loadAttachments()
  } catch (error) {
    if (error !== 'cancel') console.error(error)
  }
}

watch(() => [props.bizType, props.bizId], loadAttachments, { immediate: true })
onBeforeUnmount(closePreviewModal)
</script>
